
import { useState, useEffect } from "react";
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import { getPrivateTransactionsStatistics } from '../../../service/api/SecurityService';
import { formatAmountInThousands, formatDate } from "../../../service/utils/formatters";

const PrivateTransactionsStatisticsModal = ({ 
  show, 
  onClose, 
  securityId,
  securityName,
  securitySymbol 
}) => {
  const selectedDomain = useSelectedDomain();
  
  const [loading, setLoading] = useState(true);
  const [statisticsData, setStatisticsData] = useState(null);
  const [filters, setFilters] = useState({
    transactionType: '',
    startDate: '',
    endDate: ''
  });
  const [activeTab, setActiveTab] = useState('overview');

  // Charger les données initiales
  useEffect(() => {
    if (show && selectedDomain?.id) {
      loadStatistics();
    }
  }, [show, selectedDomain, securityId]);

  const loadStatistics = async (customFilters = null) => {
    try {
      setLoading(true);
      
      const params = {
        domainId: selectedDomain.id,
        securityId: securityId,
        ...filters,
        ...customFilters
      };
      
      // Nettoyer les paramètres vides
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await getPrivateTransactionsStatistics(params);
      
      if (response.success) {
        setStatisticsData(response.data);
      } else {
        // toast.error("Erreur lors du chargement des statistiques");
      }
    } catch (error) {
    //   console.error("Erreur:", error);
    //   toast.error("Impossible de charger les statistiques");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    loadStatistics();
  };

  const handleResetFilters = () => {
    setFilters({
      transactionType: '',
      startDate: '',
      endDate: ''
    });
    loadStatistics({
      transactionType: '',
      startDate: '',
      endDate: ''
    });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'Emission': 'primary',
      'Negociation': 'success',
      'Souscription': 'warning',
      'Transfert': 'info',
      'Rachat': 'danger'
    };
    return colors[type] || 'secondary';
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div 
        className="modal-dialog modal-custom modal-dialog-centered modal-dialog-scrollable"
      >
        <div className="modal-content border-0 shadow-lg">
          {/* En-tête du modal */}
          <div className="modal-header bg-gradient-primary text-white">
            <div className="d-flex align-items-center w-100">
              <div className="me-3">
                <div className="bg-white bg-opacity-25 rounded-circle p-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 22V2L21 12L3 22Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div className="flex-grow-1">
                <h5 className="modal-title mb-0 fw-bold">
                  <i className="fas fa-bar-chart-fill me-2"></i>
                  Analyse des Transactions
                </h5>
                <small className="text-white-80">
                  {securityName && (
                    <>
                      <strong>{securityName}</strong>
                      {securitySymbol && ` (${securitySymbol})`}
                      {selectedDomain?.name && ` • Domaine: ${selectedDomain.name}`}
                    </>
                  )}
                </small>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Barre de filtres */}
          <div className="border-bottom bg-light">
            <div className="p-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">Type de Transaction</label>
                  <select 
                    className="account-type-select"
                    value={filters.transactionType}
                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                  >
                    <option value="">Tous les types</option>
                    <option value="Emission">Émission</option>
                    <option value="Negociation">Négociation</option>
                    <option value="Souscription">Souscription</option>
                    <option value="Transfert">Transfert</option>
                    <option value="Rachat">Rachat</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">Date de début</label>
                  <input 
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">Date de fin</label>
                  <input 
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <div className="d-flex gap-2 mt-4">
                    <button 
                      className="btn btn-primary btn-sm flex-grow-1"
                      onClick={handleApplyFilters}
                    >
                      <i className="fas fa-funnel me-1"></i>
                      Appliquer
                    </button>
                    <button 
                      className="btn btn-outline-primary"
                      onClick={handleResetFilters}
                    >
                      actualiser
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Onglets */}
          <div className="border-bottom">
            <ul className="nav nav-tabs nav-tabs-custom px-3">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  <i className="fas fa-speedometer2 me-2"></i>
                  Vue d'ensemble
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'securities' ? 'active' : ''}`}
                  onClick={() => setActiveTab('securities')}
                >
                  <i className="fas fa-pie-chart-fill me-2"></i>
                  Analyse par Titre
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'types' ? 'active' : ''}`}
                  onClick={() => setActiveTab('types')}
                >
                  <i className="fas fa-diagram-3-fill me-2"></i>
                  Répartition par Type
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'timeline' ? 'active' : ''}`}
                  onClick={() => setActiveTab('timeline')}
                >
                  <i className="fas fa-graph-up me-2"></i>
                  Évolution Temporelle
                </button>
              </li>
            </ul>
          </div>

          {/* Corps du modal */}
          <div className="modal-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3 text-muted">Chargement des données statistiques...</p>
              </div>
            ) : statisticsData ? (
              <div className="p-4">
                {/* Onglet: Vue d'ensemble */}
                {activeTab === 'overview' && (
                  <div className="row">
                    {/* Cartes de statistiques principales */}
                    <div className="col-xl-12 mb-4">
                      <div className="row g-3">
                        <div className="col-xl-3 col-md-6">
                          <div className="card border-0 bg-light bg-opacity-10 border-start border-primary border-3 h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-white bg-opacity-25 text-primary">Total</span>
                                <i className="fas fa-arrow-repeat text-primary"></i>
                              </div>
                              <h3 className="fw-bold mb-1">{statisticsData.totalTransactions}</h3>
                              <p className="text-muted mb-0">Transactions</p>
                              <small className="text-primary d-block mt-2">
                                <i className="fas fa-info-circle me-1"></i>
                                Sur la période sélectionnée
                              </small>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-xl-3 col-md-6">
                          <div className="card border-0 bg-success bg-opacity-10 border-start border-success border-3 h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-success bg-opacity-25 text-success">Valeur</span>
                                <i className="fas fa-cash-coin text-success"></i>
                              </div>
                              <h3 className="fw-bold mb-1">{formatAmountInThousands(statisticsData.totalValue)}</h3>
                              <p className="text-muted mb-0">Valeur totale</p>
                              <small className="text-success d-block mt-2">
                                <i className="fas fa-currency-exchange me-1"></i>
                                Moyenne: {formatAmountInThousands(
                                  statisticsData.totalValue / statisticsData.totalTransactions
                                )}/transaction
                              </small>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-xl-3 col-md-6">
                          <div className="card border-0 bg-warning bg-opacity-10 border-start border-warning border-3 h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-warning bg-opacity-25 text-warning">Quantité</span>
                                <i className="fas fa-box-seam text-warning"></i>
                              </div>
                              <h3 className="fw-bold mb-1">{formatNumber(statisticsData.totalQuantity)}</h3>
                              <p className="text-muted mb-0">Titres échangés</p>
                              <small className="text-warning d-block mt-2">
                                <i className="fas fa-calculator me-1"></i>
                                Moyenne: {formatNumber(
                                  Math.round(statisticsData.totalQuantity / statisticsData.totalTransactions)
                                )}/transaction
                              </small>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-xl-3 col-md-6">
                          <div className="card border-0 bg-info bg-opacity-10 border-start border-info border-3 h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-info bg-opacity-25 text-info">Diversité</span>
                                <i className="fas fa-collection text-info"></i>
                              </div>
                              <h3 className="fw-bold mb-1">{statisticsData.uniqueSecuritiesCount}</h3>
                              <p className="text-muted mb-0">Titres uniques</p>
                              <small className="text-info d-block mt-2">
                                <i className="fas fa-shield-check me-1"></i>
                                {securityId ? 'Titre spécifique' : 'Variété de titres'}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Graphique de répartition */}
                    <div className="col-xl-6 mb-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white">
                          <h6 className="mb-0 fw-bold">
                            <i className="fas fa-pie-chart-fill me-2 text-primary"></i>
                            Répartition par Type de Transaction
                          </h6>
                        </div>
                        <div className="card-body">
                          {statisticsData.transactionsByType && 
                           Object.keys(statisticsData.transactionsByType).length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-borderless table-striped table-hover mb-0">
                                <thead>
                                  <tr>
                                    <th>Type</th>
                                    <th className="text-center">Transactions</th>
                                    <th className="text-end">Valeur</th>
                                    <th className="text-end">Quantité</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.values(statisticsData.transactionsByType).map((typeData, index) => (
                                    <tr key={index}>
                                      <td>
                                        <span className={`badge ${getTransactionTypeColor(typeData.transactionType)} bg-opacity-10 text-${getTransactionTypeColor(typeData.transactionType)}`}>
                                          {typeData.transactionType}
                                        </span>
                                      </td>
                                      <td className="text-center">
                                        <strong>{typeData.count}</strong>
                                        <div className="progress mt-1" style={{ height: '6px' }}>
                                          <div 
                                            className={`progress-bar bg-${getTransactionTypeColor(typeData.transactionType)}`}
                                            role="progressbar"
                                            style={{ 
                                              width: `${(typeData.count / statisticsData.totalTransactions) * 100}%` 
                                            }}
                                          ></div>
                                        </div>
                                      </td>
                                      <td className="text-end text-success fw-semibold">
                                        {formatAmountInThousands(typeData.totalValue)}
                                      </td>
                                      <td className="text-end">
                                        {formatNumber(typeData.totalQuantity)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <i className="fas fa-pie-chart text-muted fs-1"></i>
                              <p className="mt-2 text-muted">Aucune donnée de répartition disponible</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Détails du titre (si spécifique) */}
                    <div className="col-xl-6 mb-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white">
                          <h6 className="mb-0 fw-bold">
                            <i className="fas fa-info-circle-fill me-2 text-info"></i>
                            Statistiques du Titre
                          </h6>
                        </div>
                        <div className="card-body">
                          {statisticsData.securitiesStatistics && 
                           statisticsData.securitiesStatistics.length > 0 ? (
                            statisticsData.securitiesStatistics.map((securityStat, index) => (
                              <div key={index} className="mb-3">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="bg-light bg-opacity-10 rounded-5 p-2 me-3">
                                    <i className="fas fa-gem text-primary"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="mb-0 fw-bold">{securityStat.securityName}</h6>
                                    <small className="text-muted">
                                      {securityStat.symbol} • {securityStat.isin}
                                    </small>
                                  </div>
                                </div>
                                
                                <div className="row g-2">
                                  <div className="col-6">
                                    <div className="p-2 border rounded">
                                      <small className="text-muted d-block">Prix moyen</small>
                                      <strong className="text-primary">{securityStat.averagePrice?.toFixed(2) || 'N/A'}</strong>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="p-2 border rounded">
                                      <small className="text-muted d-block">Dernière transaction</small>
                                      <strong>
                                        {securityStat.lastTransactionDate ? 
                                          new Date(securityStat.lastTransactionDate).toLocaleDateString('fr-FR') : 
                                          'N/A'
                                        }
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4">
                              <i className="fas fa-gem text-muted fs-1"></i>
                              <p className="mt-2 text-muted">
                                {securityId ? 
                                  "Aucune statistique disponible pour ce titre" :
                                  "Sélectionnez un titre pour voir les détails"
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Onglet: Analyse par Titre */}
                {activeTab === 'securities' && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="mb-0 fw-bold">
                        <i className="bi bi-table me-2 text-success"></i>
                        Performance par Titre
                      </h6>
                    </div>
                    <div className="card-body p-0">
                      {statisticsData.securitiesStatistics && 
                       statisticsData.securitiesStatistics.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Titre</th>
                                <th className="text-center">Transactions</th>
                                <th className="text-end">Valeur Totale</th>
                                <th className="text-end">Quantité Totale</th>
                                <th className="text-center">Prix Moyen</th>
                                <th className="text-center">Dernière Opération</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statisticsData.securitiesStatistics.map((security, index) => (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="avatar-sm rounded bg-primary bg-opacity-10 me-2">
                                        <span className="avatar-title text-primary fw-bold">
                                          {security.securityName?.charAt(0)}
                                        </span>
                                      </div>
                                      <div>
                                        <div className="fw-semibold">{security.securityName}</div>
                                        <small className="text-muted">{security.symbol}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-primary bg-opacity-10 text-primary">
                                      {security.transactionCount}
                                    </span>
                                  </td>
                                  <td className="text-end fw-bold text-success">
                                    {formatAmountInThousands(security.totalValue)}
                                  </td>
                                  <td className="text-end">
                                    {formatNumber(security.totalQuantity)}
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-info bg-opacity-10 text-info">
                                      {security.averagePrice?.toFixed(2) || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    {security.lastTransactionDate ? 
                                      new Date(security.lastTransactionDate).toLocaleDateString('fr-FR') : 
                                      'N/A'
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="bi bi-table text-muted fs-1"></i>
                          <p className="mt-3 text-muted">Aucune donnée de performance disponible</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Onglet: Répartition par Type */}
                {activeTab === 'types' && (
                  <div className="row">
                    <div className="col-xl-8">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white">
                          <h6 className="mb-0 fw-bold">
                            <i className="bi bi-diagram-3-fill me-2 text-warning"></i>
                            Analyse Détaillée par Type
                          </h6>
                        </div>
                        <div className="card-body">
                          {statisticsData.transactionsByType && 
                           Object.keys(statisticsData.transactionsByType).length > 0 ? (
                            <div className="row">
                              {Object.values(statisticsData.transactionsByType).map((typeData, index) => (
                                <div key={index} className="col-md-6 mb-3">
                                  <div className="border rounded p-3 h-100">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className={`badge bg-${getTransactionTypeColor(typeData.transactionType)} bg-opacity-25 text-${getTransactionTypeColor(typeData.transactionType)}`}>
                                        {typeData.transactionType}
                                      </span>
                                      <span className="fw-bold">{typeData.count}</span>
                                    </div>
                                    
                                    <div className="mb-3">
                                      <div className="d-flex justify-content-between mb-1">
                                        <small className="text-muted">Valeur totale</small>
                                        <small className="fw-semibold text-success">
                                          {formatAmountInThousands(typeData.totalValue)}
                                        </small>
                                      </div>
                                      <div className="progress" style={{ height: '8px' }}>
                                        <div 
                                          className={`progress-bar bg-${getTransactionTypeColor(typeData.transactionType)}`}
                                          role="progressbar"
                                          style={{ 
                                            width: `${(typeData.totalValue / statisticsData.totalValue) * 100}%` 
                                          }}
                                        ></div>
                                      </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                      <div className="d-flex justify-content-between mb-1">
                                        <small className="text-muted">Quantité totale</small>
                                        <small className="fw-semibold">
                                          {formatNumber(typeData.totalQuantity)}
                                        </small>
                                      </div>
                                      <div className="progress" style={{ height: '8px' }}>
                                        <div 
                                          className={`progress-bar bg-${getTransactionTypeColor(typeData.transactionType)} bg-opacity-75`}
                                          role="progressbar"
                                          style={{ 
                                            width: `${(typeData.totalQuantity / statisticsData.totalQuantity) * 100}%` 
                                          }}
                                        ></div>
                                      </div>
                                    </div>
                                    
                                    <div className="d-flex justify-content-between">
                                      <small className="text-muted">Part des transactions</small>
                                      <small className="fw-bold">
                                        {((typeData.count / statisticsData.totalTransactions) * 100).toFixed(1)}%
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-5">
                              <i className="bi bi-diagram-3 text-muted fs-1"></i>
                              <p className="mt-3 text-muted">Aucune donnée de répartition disponible</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-xl-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white">
                          <h6 className="mb-0 fw-bold">
                            <i className="bi bi-info-square-fill me-2 text-info"></i>
                            Résumé des Filtres
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <small className="text-muted d-block">Période analysée</small>
                            <strong>
                              {filters.startDate ? 
                                new Date(filters.startDate).toLocaleDateString('fr-FR') : 
                                'Début'
                              } 
                              {' → '}
                              {filters.endDate ? 
                                new Date(filters.endDate).toLocaleDateString('fr-FR') : 
                                'Aujourd\'hui'
                              }
                            </strong>
                          </div>
                          
                          <div className="mb-3">
                            <small className="text-muted d-block">Type de transaction</small>
                            <strong className="text-primary">
                              {filters.transactionType || 'Tous les types'}
                            </strong>
                          </div>
                          
                          <div className="mb-3">
                            <small className="text-muted d-block">Titre analysé</small>
                            <strong>
                              {securityName || 'Tous les titres'}
                            </strong>
                          </div>
                          
                          <div className="mt-4 p-3 bg-light rounded">
                            <small className="text-muted d-block">Données générées le</small>
                            <strong>
                              {statisticsData.generatedAt ? 
                                formatDate(statisticsData.generatedAt) : 
                                'N/A'
                              }
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Onglet: Évolution Temporelle */}
                {activeTab === 'timeline' && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="mb-0 fw-bold">
                        <i className="bi bi-calendar-week-fill me-2 text-purple"></i>
                        Activité au Fil du Temps
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="text-center py-5">
                        <div className="display-1 text-purple mb-3">
                          <i className="bi bi-clock-history"></i>
                        </div>
                        <h5 className="mb-3">Analyse Temporelle</h5>
                        <p className="text-muted mb-4">
                          Cette fonctionnalité sera disponible prochainement.<br/>
                          Elle permettra de visualiser l'évolution des transactions dans le temps.
                        </p>
                        <div className="alert alert-info">
                          <i className="bi bi-lightbulb-fill me-2"></i>
                          <strong>Prochainement :</strong> Graphiques d'évolution, tendances saisonnières et prévisions
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5">
                <div className="text-muted">
                  <i className="fas fa-bar-chart fs-1 opacity-50"></i>
                  <p className="mt-3">Aucune donnée statistique disponible</p>
                  <button 
                    className="btn btn-outline-primary mt-2"
                    onClick={handleResetFilters}
                  >
                    <i className="fas fa-arrow-clockwise me-2"></i>
                    Réessayer
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pied du modal */}
          <div className="modal-footer border-top">
            <div className="d-flex justify-content-between w-100">
              <div>
                {statisticsData && (
                  <small className="text-muted">
                    <i className="fas fa-clock-history me-1"></i>
                    Dernière mise à jour: {
                      statisticsData.generatedAt ? 
                      formatDate(statisticsData.generatedAt) : 
                      'N/A'
                    }
                  </small>
                )}
              </div>
              <div className="d-flex gap-2">
                <button 
                  type="button" 
                  className="btn btn-outline-danger"
                  onClick={onClose}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateTransactionsStatisticsModal;